import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CREATORS, MODELS } from "@/data/characters";
import { getCohortMembers } from "@/lib/api";
import { windowState } from "@/lib/windows";
import { CTA } from "@/components/CTA";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { MemberCard } from "@/components/cohort/MemberCard";

const TABS = [
  { id: "all", label: "Everyone" },
  { id: "creators", label: "The Creators" },
  { id: "models", label: "The Models" },
  { id: "cohort", label: "Founding Cohort" },
];

export default function Talent() {
  const [tab, setTab] = useState("all");
  const [members, setMembers] = useState([]);
  const [membersState, setMembersState] = useState("loading");
  useEffect(() => {
    getCohortMembers()
      .then((m) => { setMembers(m); setMembersState("ready"); })
      .catch(() => setMembersState("error"));
  }, []);
  const shown = tab === "creators" ? CREATORS : tab === "models" ? MODELS : [...CREATORS, ...MODELS];

  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="The BLV Universe"
        titleLines={["Meet", "the cast."]}
        subtitle="Fourteen original illustrated characters — seven Creators, seven Models — who rep the Village across every Issue. The real Founding Cohort joins them as members are onboarded."
      />

      <section className="grain-dark border-b border-gold/25 bg-charcoal py-8" data-testid="cohort-forming-banner">
        <div className="relative mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <p className="font-display text-2xl uppercase tracking-tight text-metallic sm:text-3xl">The Village is forming.</p>
          </div>
          <p className="max-w-md font-body text-sm text-silver/75">
            Real founding cohort profiles appear here as creatives are accepted and onboarded. Until then — the cast holds it down.
          </p>
          <CTA to="/apply/student" variant="pink" data-testid="talent-apply-cta">
            {windowState("student").status === "open" ? "Submit Your Dossier" : `Sealed · Opens ${windowState("student").opensShort}`}
          </CTA>
        </div>
      </section>

      <section className="border-b border-gold/20 bg-onyx py-8">
        <div className="mx-auto flex max-w-[1400px] flex-wrap gap-2 px-4 sm:px-6" data-testid="talent-filters">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`filter-${t.id}`}
              className={`border px-4 py-1.5 font-head text-xs font-extrabold uppercase tracking-wide transition-colors ${
                tab === t.id
                  ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx"
                  : "border-silver/30 bg-charcoal text-silver hover:border-gold/60 hover:text-goldlight"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="grain-dark bg-onyx py-16 sm:py-24">
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          {tab === "cohort" ? (
            <div data-testid="cohort-members-section">
              <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-silver/40">BLV / Founding Cohort Directory · Real Members</p>
              {membersState === "loading" ? (
                <p className="mt-8 text-center font-head text-sm font-bold uppercase text-silver/40" data-testid="cohort-members-loading">Opening the directory...</p>
              ) : membersState === "error" ? (
                <p className="mt-8 text-center font-body text-sm text-silver/60" data-testid="cohort-members-error">
                  The directory couldn't load right now — refresh to try again.
                </p>
              ) : members.length === 0 ? (
                <div className="mt-8 flex min-h-[280px] flex-col items-center justify-center border border-dashed border-gold/40 bg-charcoal/50 p-10 text-center" data-testid="cohort-members-empty">
                  <p className="font-display text-3xl uppercase tracking-tight text-metallic sm:text-4xl">The roster is being written.</p>
                  <p className="mt-3 max-w-md font-body text-sm text-silver/70">
                    Real founding cohort profiles appear here as members are accepted, onboarded, and published. Yours could be first.
                  </p>
                  <CTA to="/apply/student" variant="pink" className="mt-6">Claim Your Spot</CTA>
                </div>
              ) : (
                <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {members.map((m, i) => (
                    <Reveal key={m.member_number || i} delay={(i % 4) * 0.06}>
                      <MemberCard m={m} />
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {shown.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 4) * 0.06}>
                <Link to={`/talent/${c.slug}`} data-testid={`talent-card-${c.slug}`} className="group block">
                  <motion.div whileHover={{ y: -4 }} className="overflow-hidden gold-frame bg-charcoal transition-shadow duration-300 group-hover:shadow-[0_0_35px_rgba(201,162,39,0.18)]">
                    <div className="panel-mono relative h-72 overflow-hidden">
                      <img src={c.img} alt={c.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-onyx/60 to-transparent" />
                      <div className="absolute left-2 top-2 border border-gold/50 bg-onyx/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                        {c.code}
                      </div>
                    </div>
                    <div className="border-t border-gold/25 p-4">
                      <h3 className="font-display text-3xl uppercase leading-none tracking-tight text-cream">{c.name}</h3>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gold">{c.lane}</p>
                      <p className="mt-2 font-body text-sm leading-relaxed text-silver/75">{c.bio}</p>
                      <p className="mt-3 -rotate-1 font-marker text-sm text-goldlight/90">{c.tag}</p>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
          )}
        </div>
      </section>

      <section className="grain-dark relative border-t border-gold/25 bg-charcoal py-16">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative mx-auto max-w-[900px] px-4 text-center sm:px-6">
          <Kicker>Phase 2 Teaser</Kicker>
          <p className="mt-5 font-marker text-3xl text-goldlight">BLV AVATAR — COMING SOON ✦</p>
          <p className="mx-auto mt-3 max-w-md font-body text-sm text-silver/70">
            One day, every Villager gets their own illustrated avatar in this universe. Build your portfolio first.
          </p>
        </div>
      </section>
    </div>
  );
}
